import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Grid,
    MenuItem,
    Typography,
    Divider,
    FormControlLabel,
    Checkbox
} from '@mui/material';
import api, { studentAPI } from '../services/api';
import { toast } from 'react-toastify';

const StudentForm = ({ open, handleClose, studentToEdit, refreshData }) => {
    const [formData, setFormData] = useState({
        // 1. Student Details
        firstName: '', middleName: '', lastName: '', dateOfBirth: '', placeOfBirth: '',
        gender: '', religion: '', admissionDate: '', regNo: '', 
        grBookId: '', grBookName: '', apaarId: '', penNumber: '', 
        saralId: '', enrollmentNumber: '', rollNumber: '', libraryNumber: '',
        
        // 2. Academic Details
        admittedAcademicYearId: '', classAtAdmission: '', 
        currentClassAdmissionDate: '', currentMedium: '', classId: '',
        sectionId: '',
        
        // 3. Correspondence Address
        addressStreet: '', addressCity: '', addressZipCode: '',
        addressState: 'Maharashtra', addressCountry: 'India',
        
        // 4. Permanent Address
        pAddress: '', pCity: '', pPincode: '', pState: 'Maharashtra', pCountry: 'India',
        sameAsCorrespondence: false,
        
        // 5. Father Details
        fatherName: '', fatherEducation: '', fatherOccupation: '',
        fatherIncome: '', fatherAge: '', fatherEmail: '',
        
        // 6. Mother Details
        motherName: '',
        
        // 7. Bank Details
        bankName: '', bankBranch: '', bankAccountNo: '', bankIfscCode: '', uid: '',
        
        // 8. Contact Details
        phone: '', fatherPhone: '', email: '',
        
        // 9. Previous School
        previousSchoolName: '', previousSchoolLastClass: '', motherTongue: '',
        
        // 10. Other Details
        nationality: 'Indian', category: '', caste: '', minorityType: '', concession: '',
    });

    const [classes, setClasses] = useState([]);
    const [sections, setSections] = useState([]);

    useEffect(() => {
        if (open) {
            fetchClasses();
            if (studentToEdit) {
                setFormData({
                    ...studentToEdit,
                    ...studentToEdit.user,
                    dateOfBirth: studentToEdit.user?.dateOfBirth ? studentToEdit.user.dateOfBirth.split('T')[0] : '',
                    admissionDate: studentToEdit.admissionDate ? studentToEdit.admissionDate.split('T')[0] : '',
                    currentClassAdmissionDate: studentToEdit.currentClassAdmissionDate ? studentToEdit.currentClassAdmissionDate.split('T')[0] : '',
                    classId: studentToEdit.classId || '',
                    sectionId: studentToEdit.sectionId || '',
                    sameAsCorrespondence: false
                });
            } else {
                setFormData({
                    firstName: '', middleName: '', lastName: '', dateOfBirth: '', placeOfBirth: '',
                    gender: '', religion: '', admissionDate: new Date().toISOString().split('T')[0], 
                    regNo: '', grBookId: '', grBookName: '', apaarId: '', penNumber: '', 
                    saralId: '', enrollmentNumber: '', rollNumber: '', libraryNumber: '',
                    admittedAcademicYearId: '', classAtAdmission: '', 
                    currentClassAdmissionDate: new Date().toISOString().split('T')[0], 
                    currentMedium: 'English', classId: '', sectionId: '',
                    addressStreet: '', addressCity: '', addressZipCode: '', addressState: 'Maharashtra', addressCountry: 'India',
                    pAddress: '', pCity: '', pPincode: '', pState: 'Maharashtra', pCountry: 'India',
                    sameAsCorrespondence: false,
                    fatherName: '', fatherEducation: '', fatherOccupation: '', fatherIncome: '', fatherAge: '', fatherEmail: '',
                    motherName: '',
                    bankName: '', bankBranch: '', bankAccountNo: '', bankIfscCode: '', uid: '',
                    phone: '', fatherPhone: '', email: '',
                    previousSchoolName: '', previousSchoolLastClass: '', motherTongue: 'Marathi',
                    nationality: 'Indian', category: '', caste: '', minorityType: '', concession: '',
                });
            }
        }
    }, [open, studentToEdit]);

    const fetchClasses = async () => {
        try {
            const res = await api.get('/classes');
            setClasses(res.data.data || []);
        } catch (error) {
            console.error("Failed to fetch classes");
        }
    };

    const handleClassChange = async (e) => {
        const classId = e.target.value;
        setFormData({ ...formData, classId });
        try {
            const res = await api.get(`/classes/${classId}/sections`);
            setSections(res.data.data || []);
        } catch (error) {
            console.error("Failed to fetch sections");
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') {
            const updates = { [name]: checked };
            if (name === 'sameAsCorrespondence' && checked) {
                updates.pAddress = formData.addressStreet;
                updates.pCity = formData.addressCity;
                updates.pPincode = formData.addressZipCode;
                updates.pState = formData.addressState;
                updates.pCountry = formData.addressCountry;
            }
            setFormData({ ...formData, ...updates });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleSubmit = async () => {
        try {
            const userData = {
                firstName: formData.firstName,
                middleName: formData.middleName,
                lastName: formData.lastName,
                email: formData.email,
                phone: formData.phone,
                gender: formData.gender,
                dateOfBirth: formData.dateOfBirth,
                addressStreet: formData.addressStreet,
                addressCity: formData.addressCity,
                addressState: formData.addressState,
                addressZipCode: formData.addressZipCode,
                addressCountry: formData.addressCountry,
            };

            const studentData = {
                // Section 1
                placeOfBirth: formData.placeOfBirth,
                religion: formData.religion,
                admissionDate: formData.admissionDate,
                regNo: formData.regNo,
                grBookId: formData.grBookId,
                grBookName: formData.grBookName,
                apaarId: formData.apaarId,
                penNumber: formData.penNumber,
                saralId: formData.saralId,
                enrollmentNumber: formData.enrollmentNumber,
                rollNumber: formData.rollNumber,
                libraryNumber: formData.libraryNumber,
                
                // Section 2
                admittedAcademicYearId: formData.admittedAcademicYearId || null,
                classAtAdmission: formData.classAtAdmission,
                currentClassAdmissionDate: formData.currentClassAdmissionDate,
                currentMedium: formData.currentMedium,
                classId: formData.classId,
                sectionId: formData.sectionId,
                
                // Section 4
                pAddress: formData.pAddress,
                pCity: formData.pCity,
                pPincode: formData.pPincode,
                pState: formData.pState,
                pCountry: formData.pCountry,
                
                // Section 5
                fatherName: formData.fatherName,
                fatherEducation: formData.fatherEducation,
                fatherOccupation: formData.fatherOccupation,
                fatherIncome: formData.fatherIncome,
                fatherAge: formData.fatherAge,
                fatherEmail: formData.fatherEmail,
                fatherPhone: formData.fatherPhone,
                
                // Section 6, 7
                motherName: formData.motherName,
                bankName: formData.bankName,
                bankBranch: formData.bankBranch,
                bankAccountNo: formData.bankAccountNo,
                bankIfscCode: formData.bankIfscCode,
                uid: formData.uid,
                
                // Section 9, 10
                previousSchoolName: formData.previousSchoolName,
                previousSchoolLastClass: formData.previousSchoolLastClass,
                motherTongue: formData.motherTongue,
                nationality: formData.nationality,
                category: formData.category,
                caste: formData.caste,
                minorityType: formData.minorityType,
                concession: formData.concession,
            };

            const payload = { userData, studentData };

            if (studentToEdit) {
                await studentAPI.update(studentToEdit.id, payload);
                toast.success("Student Updated Successfully");
            } else {
                await studentAPI.create(payload);
                toast.success("Student Created Successfully");
            }
            refreshData();
            handleClose();
        } catch (error) {
            console.error("Submit Error:", error);
            toast.error(error.response?.data?.message || "Operation Failed");
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
            <DialogTitle>{studentToEdit ? 'Edit Student Details' : 'Add New Student'}</DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={3}>
                    {/* SECTION 1: Student Details */}
                    <Grid item xs={12}><Typography variant="h6" color="primary">Student Details</Typography></Grid>
                    <Grid item xs={4}><TextField fullWidth label="First Name" name="firstName" value={formData.firstName} onChange={handleChange} /></Grid>
                    <Grid item xs={4}><TextField fullWidth label="Middle Name" name="middleName" value={formData.middleName || ''} onChange={handleChange} /></Grid>
                    <Grid item xs={4}><TextField fullWidth label="Last Name" name="lastName" value={formData.lastName} onChange={handleChange} /></Grid>
                    <Grid item xs={3}><TextField fullWidth label="Date of Birth" type="date" name="dateOfBirth" InputLabelProps={{ shrink: true }} value={formData.dateOfBirth} onChange={handleChange} /></Grid>
                    <Grid item xs={3}><TextField fullWidth label="Birth Place" name="placeOfBirth" value={formData.placeOfBirth} onChange={handleChange} /></Grid>
                    
                    <Grid item xs={3}>
                        <TextField select fullWidth label="Gender" name="gender" value={formData.gender} onChange={handleChange}>
                            <MenuItem value="male">Male</MenuItem>
                            <MenuItem value="female">Female</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={3}><TextField fullWidth label="Religion" name="religion" value={formData.religion} onChange={handleChange} /></Grid>
                    <Grid item xs={3}><TextField fullWidth label="Admission Date" type="date" name="admissionDate" InputLabelProps={{ shrink: true }} value={formData.admissionDate} onChange={handleChange} /></Grid>
                    <Grid item xs={3}><TextField fullWidth label="Reg No." name="regNo" value={formData.regNo} onChange={handleChange} /></Grid>
                    <Grid item xs={3}><TextField fullWidth label="Library Number" name="libraryNumber" value={formData.libraryNumber} onChange={handleChange} /></Grid>
                    
                    <Grid item xs={3}><TextField fullWidth label="Gr. Book Id" name="grBookId" value={formData.grBookId} onChange={handleChange} /></Grid>
                    <Grid item xs={3}><TextField fullWidth label="Gr. Book Name" name="grBookName" value={formData.grBookName} onChange={handleChange} /></Grid>
                    <Grid item xs={3}><TextField fullWidth label="Apaar Id" name="apaarId" value={formData.apaarId} onChange={handleChange} /></Grid>
                    <Grid item xs={3}><TextField fullWidth label="Pen No." name="penNumber" value={formData.penNumber} onChange={handleChange} /></Grid>
                    
                    <Grid item xs={3}><TextField fullWidth label="Saral Id" name="saralId" value={formData.saralId} onChange={handleChange} /></Grid>
                    <Grid item xs={3}><TextField fullWidth label="Enrollment Number" name="enrollmentNumber" value={formData.enrollmentNumber} onChange={handleChange} /></Grid>
                    <Grid item xs={3}><TextField fullWidth label="Roll Call No." name="rollNumber" value={formData.rollNumber} onChange={handleChange} /></Grid>
                    <Grid item xs={3} />

                    <Grid item xs={12}><Divider /></Grid>

                    {/* SECTION 2: Academic Details */}
                    <Grid item xs={12}><Typography variant="h6" color="primary">Academic Details</Typography></Grid>
                    <Grid item xs={3}><TextField fullWidth label="Admitted Academic Year Ref" name="admittedAcademicYearId" type="number" placeholder="Year ID" value={formData.admittedAcademicYearId} onChange={handleChange} /></Grid>
                    <Grid item xs={3}><TextField fullWidth label="Admitted Class (Prev/String)" name="classAtAdmission" value={formData.classAtAdmission} onChange={handleChange} /></Grid>
                    <Grid item xs={3}><TextField fullWidth label="Current Medium" name="currentMedium" value={formData.currentMedium} onChange={handleChange} /></Grid>
                    <Grid item xs={3}><TextField fullWidth label="Admission Date in Current Class" type="date" name="currentClassAdmissionDate" InputLabelProps={{ shrink: true }} value={formData.currentClassAdmissionDate} onChange={handleChange} /></Grid>

                    <Grid item xs={3}>
                        <TextField select fullWidth label="Current Class" name="classId" value={formData.classId} onChange={handleClassChange}>
                            {classes.map((cls) => (
                                <MenuItem key={cls.id} value={cls.id}>{cls.name}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={3}>
                        <TextField select fullWidth label="Current Section" name="sectionId" value={formData.sectionId} onChange={handleChange}>
                            {sections.map((sec) => (
                                <MenuItem key={sec.id} value={sec.id}>{sec.name}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12}><Divider /></Grid>

                    {/* SECTION 3: Correspondence Address */}
                    <Grid item xs={12}><Typography variant="h6" color="primary">Correspondence Address</Typography></Grid>
                    <Grid item xs={6}><TextField fullWidth label="Address" name="addressStreet" value={formData.addressStreet} onChange={handleChange} /></Grid>
                    <Grid item xs={3}><TextField fullWidth label="City" name="addressCity" value={formData.addressCity} onChange={handleChange} /></Grid>
                    <Grid item xs={3}><TextField fullWidth label="Pincode" name="addressZipCode" value={formData.addressZipCode} onChange={handleChange} /></Grid>
                    <Grid item xs={3}><TextField fullWidth label="State" name="addressState" value={formData.addressState} onChange={handleChange} /></Grid>
                    <Grid item xs={3}><TextField fullWidth label="Country" name="addressCountry" value={formData.addressCountry} onChange={handleChange} /></Grid>

                    <Grid item xs={12}><Divider /></Grid>

                    {/* SECTION 4: Permanent Address */}
                    <Grid item xs={12}>
                        <Typography variant="h6" color="primary" sx={{ display: 'inline-block', mr: 2 }}>Permanent Address</Typography>
                        <FormControlLabel
                            control={<Checkbox checked={formData.sameAsCorrespondence} onChange={handleChange} name="sameAsCorrespondence" />}
                            label="Same as Correspondence Address"
                        />
                    </Grid>
                    <Grid item xs={6}><TextField fullWidth label="Permanent Address" name="pAddress" value={formData.pAddress} onChange={handleChange} disabled={formData.sameAsCorrespondence} /></Grid>
                    <Grid item xs={3}><TextField fullWidth label="Permanent City" name="pCity" value={formData.pCity} onChange={handleChange} disabled={formData.sameAsCorrespondence} /></Grid>
                    <Grid item xs={3}><TextField fullWidth label="Permanent Pincode" name="pPincode" value={formData.pPincode} onChange={handleChange} disabled={formData.sameAsCorrespondence} /></Grid>
                    <Grid item xs={3}><TextField fullWidth label="Permanent State" name="pState" value={formData.pState} onChange={handleChange} disabled={formData.sameAsCorrespondence} /></Grid>
                    <Grid item xs={3}><TextField fullWidth label="Permanent Country" name="pCountry" value={formData.pCountry} onChange={handleChange} disabled={formData.sameAsCorrespondence} /></Grid>

                    <Grid item xs={12}><Divider /></Grid>

                    {/* SECTION 5: Father Details */}
                    <Grid item xs={12}><Typography variant="h6" color="primary">Father Details</Typography></Grid>
                    <Grid item xs={3}><TextField fullWidth label="Father's Name" name="fatherName" value={formData.fatherName} onChange={handleChange} /></Grid>
                    <Grid item xs={3}><TextField fullWidth label="Father's Education" name="fatherEducation" value={formData.fatherEducation} onChange={handleChange} /></Grid>
                    <Grid item xs={3}><TextField fullWidth label="Father's Occupation" name="fatherOccupation" value={formData.fatherOccupation} onChange={handleChange} /></Grid>
                    <Grid item xs={3}><TextField fullWidth label="Father's Income" name="fatherIncome" value={formData.fatherIncome} onChange={handleChange} /></Grid>
                    <Grid item xs={3}><TextField fullWidth label="Father's Age" type="number" name="fatherAge" value={formData.fatherAge} onChange={handleChange} /></Grid>
                    <Grid item xs={3}><TextField fullWidth label="Father's Email" type="email" name="fatherEmail" value={formData.fatherEmail} onChange={handleChange} /></Grid>

                    <Grid item xs={12}><Divider /></Grid>

                    {/* SECTION 6: Mother Details */}
                    <Grid item xs={12}><Typography variant="h6" color="primary">Mother Details</Typography></Grid>
                    <Grid item xs={4}><TextField fullWidth label="Mother's Name" name="motherName" value={formData.motherName} onChange={handleChange} /></Grid>

                    <Grid item xs={12}><Divider /></Grid>

                    {/* SECTION 7: Bank Details */}
                    <Grid item xs={12}><Typography variant="h6" color="primary">Bank Details</Typography></Grid>
                    <Grid item xs={3}><TextField fullWidth label="Bank Name" name="bankName" value={formData.bankName} onChange={handleChange} /></Grid>
                    <Grid item xs={3}><TextField fullWidth label="Branch Name" name="bankBranch" value={formData.bankBranch} onChange={handleChange} /></Grid>
                    <Grid item xs={3}><TextField fullWidth label="Account No." name="bankAccountNo" value={formData.bankAccountNo} onChange={handleChange} /></Grid>
                    <Grid item xs={3}><TextField fullWidth label="Bank IFSC Code" name="bankIfscCode" value={formData.bankIfscCode} onChange={handleChange} /></Grid>
                    <Grid item xs={3}><TextField fullWidth label="Aadhar Card No." name="uid" value={formData.uid} onChange={handleChange} /></Grid>

                    <Grid item xs={12}><Divider /></Grid>

                    {/* SECTION 8: Contact Details */}
                    <Grid item xs={12}><Typography variant="h6" color="primary">Contact Details</Typography></Grid>
                    <Grid item xs={4}><TextField fullWidth label="Student Mobile" name="phone" value={formData.phone} onChange={handleChange} inputProps={{ maxLength: 10, inputMode: 'numeric', pattern: '[0-9]{10}' }} helperText="Enter 10 digits" /></Grid>
                    <Grid item xs={4}><TextField fullWidth label="Father Mobile" name="fatherPhone" value={formData.fatherPhone} onChange={handleChange} inputProps={{ maxLength: 10, inputMode: 'numeric', pattern: '[0-9]{10}' }} helperText="Enter 10 digits" /></Grid>
                    <Grid item xs={4}><TextField fullWidth label="Email Id" type="email" name="email" value={formData.email} onChange={handleChange} /></Grid>

                    <Grid item xs={12}><Divider /></Grid>

                    {/* SECTION 9: Previous School */}
                    <Grid item xs={12}><Typography variant="h6" color="primary">Previous School</Typography></Grid>
                    <Grid item xs={4}><TextField fullWidth label="Previous School Name" name="previousSchoolName" value={formData.previousSchoolName} onChange={handleChange} /></Grid>
                    <Grid item xs={4}><TextField fullWidth label="Class In Previous School" name="previousSchoolLastClass" value={formData.previousSchoolLastClass} onChange={handleChange} /></Grid>
                    <Grid item xs={4}><TextField fullWidth label="Mother Tongue" name="motherTongue" value={formData.motherTongue} onChange={handleChange} /></Grid>

                    <Grid item xs={12}><Divider /></Grid>

                    {/* SECTION 10: Other Details */}
                    <Grid item xs={12}><Typography variant="h6" color="primary">Other Details</Typography></Grid>
                    <Grid item xs={3}><TextField fullWidth label="Nationality" name="nationality" value={formData.nationality} onChange={handleChange} /></Grid>
                    <Grid item xs={3}>
                        <TextField select fullWidth label="Category" name="category" value={formData.category} onChange={handleChange}>
                            <MenuItem value="OPEN">OPEN</MenuItem>
                            <MenuItem value="OBC">OBC</MenuItem>
                            <MenuItem value="SC">SC</MenuItem>
                            <MenuItem value="ST">ST</MenuItem>
                            <MenuItem value="VJ-A">VJ-A</MenuItem>
                            <MenuItem value="NT-B">NT-B</MenuItem>
                            <MenuItem value="NT-C">NT-C</MenuItem>
                            <MenuItem value="NT-D">NT-D</MenuItem>
                            <MenuItem value="SBC">SBC</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={3}><TextField fullWidth label="Caste" name="caste" value={formData.caste} onChange={handleChange} /></Grid>
                    <Grid item xs={3}><TextField fullWidth label="Minority Type" name="minorityType" value={formData.minorityType} onChange={handleChange} /></Grid>
                    <Grid item xs={3}><TextField fullWidth label="Concession" name="concession" value={formData.concession} onChange={handleChange} /></Grid>

                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" color="primary">Save Student</Button>
            </DialogActions>
        </Dialog>
    );
};

export default StudentForm;
